import React from "react";
import { Link } from "react-router-dom";
import LegalPage, { InfoCard } from "@/components/LegalPage";
import {
  Coins, Sparkles, RefreshCcw, Wallet, AlertTriangle, MessageCircle, Info,
} from "lucide-react";

export default function Refunds() {
  const sections = [
    {
      id: "gold-coins", title: "Gold Coin Purchases", icon: Coins, highlight: "orange",
      body: (
        <>
          <InfoCard icon={AlertTriangle} title="⚠ Non-refundable, non-transferable, non-withdrawable" tone="pink">
            <p>
              Gold Coins are a <strong>closed-loop prepaid platform credit</strong>. They are non-refundable, non-transferable between users,
              non-withdrawable to cash, and have no monetary value outside MystiCast. Coins cannot be sent, traded, or gifted from one viewer
              to another and cannot leave the platform. Analogous to Twitch Bits or YouTube Super Chat.
            </p>
          </InfoCard>
          <p>
            Gold Coins are purchased with real money and used solely to send one-way tips to verified creators or unlock cosmetic
            in-platform items. Once purchased, Gold Coins are <strong>generally non-refundable</strong>. Exceptions:
          </p>
          <InfoCard icon={Info} title="✓ When we refund" tone="volt">
            <ul>
              <li>Duplicate/erroneous charges caused by a Platform bug (refunded within 5–10 business days).</li>
              <li>Refunds required by consumer protection law (e.g., 14-day EU right-of-withdrawal <em>before</em> the coins are used).</li>
              <li>Fraudulent or unauthorized charges — report immediately to <Link to="/contact">Support</Link>.</li>
            </ul>
          </InfoCard>
          <p>
            Once you spend Gold Coins by tipping a creator, the transaction is final and cannot be reversed by MystiCast.
            Purchased coins that remain unspent expire after 12 months of continuous account inactivity.
          </p>
        </>
      ),
    },
    {
      id: "sparks", title: "Sparks", icon: Sparkles,
      body: (
        <p>
          Sparks are earned through engagement — watching streams, playing games, chatting. Sparks have no monetary value,
          cannot be purchased or withdrawn, and are not refundable in any circumstance.
        </p>
      ),
    },
    {
      id: "subscriptions", title: "Subscriptions", icon: RefreshCcw,
      body: (
        <p>
          Channel subscriptions renew monthly by default. You can cancel any time via Account &rarr; Subscription;
          access continues until the current period ends. Partial-month refunds are not offered but can be requested for account-related errors.
        </p>
      ),
    },
    {
      id: "payouts", title: "Streamer Payouts", icon: Wallet, highlight: "volt",
      body: (
        <>
          <InfoCard icon={Coins} title="💯 100% to Streamers" tone="volt">
            <p>Streamers receive <strong>100% of the tips</strong> sent to them. Stripe processing fees are absorbed by the Platform.</p>
          </InfoCard>
          <ul>
            <li>Payouts require completed <strong>Stripe Identity</strong> verification and a connected Stripe payout account.</li>
            <li>Minimum payout threshold: <strong>$25</strong>.</li>
            <li>Standard payout cadence: weekly, subject to holds for fraud/chargeback review.</li>
            <li>Tax reporting: creators earning above local thresholds may receive tax forms (1099-K in the US, equivalents elsewhere).</li>
          </ul>
        </>
      ),
    },
    {
      id: "chargebacks", title: "Chargebacks", icon: AlertTriangle, highlight: "pink",
      body: (
        <p>
          Filing a chargeback for a legitimate purchase will result in the corresponding Gold Coins being clawed back and, at our discretion,
          account suspension. If you have a dispute, please <strong>contact us first</strong> — most issues are resolved faster than a chargeback cycle.
        </p>
      ),
    },
    {
      id: "contact", title: "Contact", icon: MessageCircle,
      body: (<p>Billing or payout questions? Email <a href="mailto:payments@mysticast.tv">payments@mysticast.tv</a> with your transaction ID from the receipt email, or use our <Link to="/contact">Contact form</Link>.</p>),
    },
  ];

  return (
    <LegalPage
      icon={Coins}
      label="REFUNDS & PAYOUTS"
      title="Refunds & Payouts"
      intro="Money in, money out — how it works on MystiCast."
      updated="February 2026"
      sections={sections}
      contact={{
        title: "Billing question or refund request?",
        body: "Email payments@mysticast.tv with your transaction ID from the receipt so we can locate the charge fast.",
        cta: { label: "payments@mysticast.tv", to: "mailto:payments@mysticast.tv", external: true },
      }}
    />
  );
}
